import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { childAPI, consentAPI, parentAPI } from '../utils/api';
import type { Child } from '../types/child';
import type { Parent } from '../types/parent';
import type { ConsentForm, ConsentFormCreateRequest } from '../types/billing';

const emptyForm: ConsentFormCreateRequest = { parent_id: 0, child_id: 0, consent_type: 'photo', granted: true };

const formatDate = (value: string | null) => value ? new Date(value).toLocaleDateString() : '-';

const extractConsentList = (response: any): ConsentForm[] => {
  const candidates = [response?.consent_forms, response?.consents, response?.data];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (Array.isArray(candidate?.data)) return candidate.data;
  }
  return [];
};

const ConsentForms: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'consentForms' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const { canManageConsent } = useRolePermissions();
  const nurseryId = currentNursery?.nursery_id || '';
  const [consents, setConsents] = useState<ConsentForm[]>([]);
  const [parents, setParents] = useState<Parent[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [form, setForm] = useState<ConsentFormCreateRequest>(emptyForm);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!nurseryId) return;
    setLoading(true);
    setError(null);
    try {
      const [consentResponse, parentResponse, childResponse] = await Promise.all([
        consentAPI.list(nurseryId),
        parentAPI.list(nurseryId),
        childAPI.getChildren(nurseryId, true),
      ]);
      setConsents(extractConsentList(consentResponse));
      setParents(parentResponse.parents || parentResponse.data || []);
      setChildren(childResponse.children || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [nurseryId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setForm({
      parent_id: parents[0]?.id || 0,
      child_id: children[0]?.id || 0,
      consent_type: 'photo',
      granted: true,
    });
    setShowModal(true);
  };

  const createConsent = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!nurseryId || !form.parent_id || !form.child_id) return;
    setSaving(true);
    setError(null);
    try {
      await consentAPI.create(nurseryId, form);
      setShowModal(false);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const updateGrant = async (consent: ConsentForm) => {
    if (!nurseryId) return;
    setActionLoadingId(consent.id);
    setError(null);
    try {
      await consentAPI.revoke(nurseryId, consent.id);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || t('updateFailed'));
    } finally {
      setActionLoadingId(null);
    }
  };

  if (!canManageConsent) {
    return <Layout><Container fluid className="py-4"><Alert variant="danger">{t('noAccess')}</Alert></Container></Layout>;
  }

  return (
    <Layout>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div><h2 className="fw-bold text-primary mb-1">{t('title')}</h2><div className="text-muted">{currentNursery?.name || nurseryId}</div></div>
          <Button onClick={openCreate} disabled={!nurseryId || parents.length === 0 || children.length === 0}>{t('addConsent')}</Button>
        </div>
        {!nurseryId && <Alert variant="warning">{t('selectNursery')}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0 align-middle">
              <thead className="table-light"><tr><th>{tc('labels.child')}</th><th>{t('parent')}</th><th>{tc('labels.type')}</th><th>{tc('labels.status')}</th><th>{t('grantedAt')}</th><th>{t('revokedAt')}</th><th className="text-end">{tc('labels.actions')}</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={7} className="text-center py-5"><Spinner animation="border" /></td></tr> : consents.map((consent) => (
                  <tr key={consent.id}>
                    <td>{consent.child?.name || '-'}</td>
                    <td>{consent.parent?.name || '-'}</td>
                    <td className="text-capitalize">{t(`types.${consent.consent_type}`, { defaultValue: consent.consent_type })}</td>
                    <td><Badge bg={consent.granted ? 'success' : 'secondary'}>{consent.granted ? t('granted') : t('revoked')}</Badge></td>
                    <td>{formatDate(consent.granted_at)}</td>
                    <td>{formatDate(consent.revoked_at)}</td>
                    <td className="text-end">
                     {!consent.revoked_at && (
                      <Button size="sm" variant={consent.granted ? 'outline-danger' : 'outline-success'} disabled={actionLoadingId === consent.id} onClick={() => updateGrant(consent)}>
                        {actionLoadingId === consent.id ? <Spinner size="sm" animation="border" /> : t('revoke')}
                      </Button>
                     )}
                    </td>
                  </tr>
                ))}
                {!loading && consents.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-5">{t('noConsentForms')}</td></tr>}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Form onSubmit={createConsent}>
            <Modal.Header closeButton><Modal.Title>{t('addConsent')}</Modal.Title></Modal.Header>
            <Modal.Body>
              <Row>
                <Col md={12}><Form.Group className="mb-3"><Form.Label>{t('parent')}</Form.Label><Form.Select required value={form.parent_id || ''} onChange={(e) => setForm({ ...form, parent_id: Number(e.target.value) })}><option value="">{t('selectParent')}</option>{parents.map((parent) => <option key={parent.id} value={parent.id}>{parent.name}</option>)}</Form.Select></Form.Group></Col>
                <Col md={12}><Form.Group className="mb-3"><Form.Label>{tc('labels.child')}</Form.Label><Form.Select required value={form.child_id || ''} onChange={(e) => setForm({ ...form, child_id: Number(e.target.value) })}><option value="">{t('selectChild')}</option>{children.map((child) => <option key={child.id} value={child.id}>{child.name}</option>)}</Form.Select></Form.Group></Col>
                <Col md={12}><Form.Group className="mb-3"><Form.Label>{t('consentType')}</Form.Label><Form.Select value={form.consent_type} onChange={(e) => setForm({ ...form, consent_type: e.target.value as ConsentForm['consent_type'] })}><option value="photo">{t('types.photo')}</option><option value="data">{t('types.data')}</option><option value="pickup">{t('types.pickup')}</option></Form.Select></Form.Group></Col>
                <Col md={12}><Form.Check type="checkbox" label={t('granted')} checked={!!form.granted} onChange={(e) => setForm({ ...form, granted: e.target.checked })} /></Col>
              </Row>
            </Modal.Body>
            <Modal.Footer><Button variant="secondary" onClick={() => setShowModal(false)}>{tc('buttons.cancel')}</Button><Button type="submit" disabled={saving}>{saving ? <Spinner size="sm" animation="border" /> : tc('buttons.save')}</Button></Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </Layout>
  );
};

export default ConsentForms;
